import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { KiroToolSummary, ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { KIRO_METRIC_INFO } from '../metricInfo';
import { ChartCard } from './ChartCard';
import { EfficiencyGauge } from './EfficiencyGauge';
import { InfoTooltip } from './InfoTooltip';
import { KiroDrillDownTable } from './KiroDrillDownTable';
import { KpiCard } from './KpiCard';

const AGENT_MODE_LABELS: Record<string, string> = { vibe: 'Vibe', spec: 'Spec', cli: 'CLI', other: 'Other' };

/**
 * The Kiro-specific body of the dashboard, for KiroToolSummary. Kiro writes
 * no token counts, so every token figure here is an estimate and labelled
 * as one, and there is no cost: Kiro bills in plan credits (see
 * collector/docs/ARCHITECTURE.md "The Kiro collector").
 */
export function KiroDashboardBody({
  toolData,
  developersCount,
  coverage,
  SectionHeading,
}: {
  toolData: KiroToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupKiroTimeSeries(toolData.timeSeries);
  const turns = toolData.byModel.reduce((sum, m) => sum + m.events, 0);
  const estimatedInput = toolData.byModel.reduce((sum, m) => sum + m.estimatedInputTokens, 0);
  const estimatedOutput = toolData.byModel.reduce((sum, m) => sum + m.estimatedOutputTokens, 0);
  const autopilotTurns = toolData.byAgentMode.reduce((sum, m) => sum + m.autopilotEvents, 0);
  const ideTurns = toolData.byAgentMode.filter((m) => m.agentMode !== 'cli').reduce((sum, m) => sum + m.events, 0);
  const autopilotPercent = ideTurns > 0 ? Math.round((autopilotTurns / ideTurns) * 100) : 0;
  const byAgentMode = toolData.byAgentMode.map((m) => ({ ...m, label: AGENT_MODE_LABELS[m.agentMode] ?? m.agentMode }));

  return (
    <>
      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KpiCard
            label="Estimated tokens"
            value={(estimatedInput + estimatedOutput).toLocaleString()}
            meta={`${estimatedInput.toLocaleString()} in · ${estimatedOutput.toLocaleString()} out, from visible text`}
            flag="Estimate"
            info={KIRO_METRIC_INFO.estimatedTokens}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Turns"
            value={turns.toLocaleString()}
            meta="Completed prompt-and-reply turns in range"
            info={KIRO_METRIC_INFO.usageByModel}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Sessions"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Kiro IDE chats and CLI sessions"
            info={KIRO_METRIC_INFO.sessions}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active minutes"
            value={toolData.activity.activeMinutes.toLocaleString()}
            meta="Merged bursts of activity"
            info={KIRO_METRIC_INFO.activeMinutes}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={KIRO_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Tool calls"
            value={toolData.activity.toolCalls.toLocaleString()}
            meta={`${Math.round(toolData.activity.toolErrorRate * 1000) / 10}% failed`}
            info={KIRO_METRIC_INFO.toolCalls}
            accentClassName="bg-info"
          />
          {/* A plain card, not a gauge: autopilot use is a working style, not a score. */}
          <KpiCard
            label="Autopilot share"
            value={ideTurns > 0 ? String(autopilotPercent) : '—'}
            unit={ideTurns > 0 ? '%' : undefined}
            meta="Kiro IDE turns run with autopilot on"
            info={KIRO_METRIC_INFO.byAgentMode}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={KIRO_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <p className="mt-3 flex items-start gap-1.5 rounded-card border border-border bg-surface-alt px-4 py-3 text-xs text-text-muted">
          <span>
            <strong className="font-medium text-text-default">No cost shown.</strong> Kiro bills in plan credits, not per token, and its
            token figures are estimates, so Kiro is not included in Spend or any cost total.
          </span>
          <InfoTooltip text={KIRO_METRIC_INFO.noCost} />
        </p>
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Turns, sessions, and tool calls per day, corrected to max() per device"
            info={KIRO_METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-7"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grouped}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="requests" fill="var(--primary-blue)" name="Turns" />
                <Bar dataKey="sessions" fill="var(--tool-kiro)" name="Sessions" />
                <Bar dataKey="toolCalls" fill="var(--info)" name="Tool calls" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Tool calls by kind"
            caption="Categories only — tool names are never collected"
            info={KIRO_METRIC_INFO.toolCategories}
            className="lg:col-span-5"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.toolCategories} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis type="number" {...chartAxisTickProps} />
                <YAxis type="category" dataKey="category" width={80} {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Bar dataKey="calls" fill="var(--tool-kiro)" name="Tool calls" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Usage by model"
            caption="Estimated input and output tokens per model"
            info={KIRO_METRIC_INFO.usageByModel}
            className="lg:col-span-6"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.byModel}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="model" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="estimatedInputTokens" stackId="tokens" fill="var(--primary-blue)" name="Est. input" />
                <Bar dataKey="estimatedOutputTokens" stackId="tokens" fill="var(--tool-kiro)" name="Est. output" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Agent mode"
            caption="Turns per mode, and how many ran on autopilot"
            info={KIRO_METRIC_INFO.byAgentMode}
            className="lg:col-span-3"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byAgentMode}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="label" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="events" fill="var(--primary-blue)" name="Turns" />
                <Bar dataKey="autopilotEvents" fill="var(--tool-kiro)" name="Autopilot" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Turns by surface"
            caption="Kiro IDE or Kiro CLI"
            info={KIRO_METRIC_INFO.bySurface}
            className="lg:col-span-3"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.bySurface}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="surface" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="activeDevices" fill="var(--primary-blue)" name="Active devices" />
                <Bar dataKey="totalEvents" fill="var(--tool-kiro)" name="Turns" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      <section>
        <KiroDrillDownTable breakdown={toolData.modelDeveloperBreakdown} info={KIRO_METRIC_INFO.drillDown} />
      </section>
    </>
  );
}

function groupKiroTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; requests: number; sessions: number; toolCalls: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, requests: 0, sessions: 0, toolCalls: 0 };
    if (point.metricName === 'kiro.requests') row.requests = point.value;
    if (point.metricName === 'kiro.sessions') row.sessions = point.value;
    if (point.metricName === 'kiro.tool.calls') row.toolCalls = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
